import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Briefcase, CurrencyDollar, ChatCircle, ArrowRight, Plus } from "@phosphor-icons/react";

const STATUS_COLOR = {
  open: "bg-slate-100 text-slate-700",
  awarded: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-purple-100 text-purple-700",
  released: "bg-green-100 text-green-700",
  disputed: "bg-red-100 text-red-700",
};

export default function VirtualClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ active_jobs: [], completed_jobs: [], stats: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login?redirect=/virtual/dashboard/client"); return; }
    api.get("/virtual/dashboard/client").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
            <h1 className="font-display text-4xl mt-2">My virtual projects</h1>
          </div>
          <Link to="/virtual/jobs/new" className="bg-brand text-white px-5 py-3 font-semibold hover:bg-[#122148] inline-flex items-center gap-2" data-testid="dash-new-job-btn">
            <Plus size={16} weight="bold" /> Post new project
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            ["Total projects", data.stats.total_jobs || 0],
            ["Active", data.stats.active || 0],
            ["Completed", data.stats.completed || 0],
            ["Total spent (CAD)", `$${(data.stats.total_spent_cad || 0).toFixed(2)}`],
          ].map(([label, value]) => (
            <div key={label} className="border border-slate-200 p-4" data-testid={`cstat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
              <div className="text-2xl font-display mt-1">{value}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl mb-4">Active projects</h2>
        {data.active_jobs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 mb-10">
            <Briefcase size={24} className="mx-auto text-slate-400 mb-2" />
            No active projects. <Link to="/virtual/jobs/new" className="text-brand font-semibold">Post your first one</Link>.
          </div>
        ) : (
          <div className="space-y-3 mb-10">
            {data.active_jobs.map((j) => (
              <Link key={j.job_id} to={`/virtual/jobs/${j.job_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`cjob-${j.job_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{j.service_type} · ${j.budget_min}–${j.budget_max} · {j.application_count || 0} apps</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${STATUS_COLOR[j.status] || "bg-slate-100"}`}>{j.status.replace("_", " ")}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {data.completed_jobs.length > 0 && <>
          <h2 className="font-display text-2xl mb-4">Completed</h2>
          <div className="space-y-3">
            {data.completed_jobs.map((j) => (
              <Link key={j.job_id} to={`/virtual/jobs/${j.job_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`cjob-done-${j.job_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-1">Released · ${j.budget_min}–${j.budget_max}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-green-100 text-green-700">released</span>
                </div>
              </Link>
            ))}
          </div>
        </>}
      </section>
      <Footer />
    </div>
  );
}
